// Style/layer TABLE reducer. Phase 5 converted the four ENTITY-REMAP paths
// (deleteDimensionStyle / renameDimensionStyle / deleteMultileaderStyle /
// deleteLayer) from inline mutation into the canonical `ReducerMutationResult`
// contract so the cache + entity metadata index stay in sync via `commitMutation`.
// T7 (agent-bridge layer-mgmt) added a FIFTH — `header/rename-layer` — mirroring
// `header/rename-dimension-style` exactly (table rename + cascade rename of
// every referencing entity, here `entity.layer` instead of `Dimension.styleName`).
//
// Lane J (D3-1) — the eight SIBLING direct-set setters (addDimensionStyle /
// updateDimensionStyle / addMultileaderStyle / updateMultileaderStyle /
// updateTextStyle / addLayer / updateLayer / setLayoutPlotSettings) are ALSO
// routed through here now: they formerly poked `geometryDocument.styleTables` /
// `.layouts` straight via zustand `set`, bypassing dispatch → `computeInverse` →
// history.past, so their edits were NOT undo-able. Moving the mutation into this
// reducer (the Command-Engine chokepoint) gives each a history entry + a
// snapshot undo inverse (`header/restore-style-tables-snapshot`, restored here),
// per the CLAUDE.md rule "любые изменения геометрии только через Command Engine".
//
// Scope now:
//   ENTITY-REMAP (styleTables + spaces[].entities[]):
//     - header/delete-dimension-style   → Dimension.styleName  → "Standard"
//     - header/rename-dimension-style   → Dimension.styleName  → newName
//     - header/delete-multileader-style → Multileader.styleName → "Standard"
//     - header/delete-layer             → entity.layer          → "0"
//     - header/rename-layer             → entity.layer          → newName (T7)
//     - header/update-text-style       → MTEXT groups 42/43 → 0 (@codex PR
//                                         #794 round 15 P2; ONLY when the edit
//                                         changes a LAYOUT-affecting style
//                                         field, and it also sweeps BLOCK-
//                                         DEFINITION MTEXT children)
//   STYLE-TABLE-ONLY (styleTables[] append / merge, no entity remap):
//     - header/add-{dimension,multileader}-style, header/add-layer
//     - header/update-{dimension,multileader}-style, header/update-layer
//     - header/update-text-style, when the edit is NOT layout-affecting
//   LAYOUT-PLOT (layouts + layoutsBySpaceId):
//     - header/set-layout-plot-settings  (PDF-6.6 per-layout page setup)
//   UNDO INVERSE (history-internal snapshot restore):
//     - header/restore-style-tables-snapshot
//
// T7 (agent-bridge) fail-closed hardening: `appendStyleTableEntry` (the shared
// `add*` forward body) now REJECTS a duplicate name as a silent no-op instead of
// blindly appending. Before T7 this was safe ONLY because every `add*` caller was
// a validated editorStore wrapper (addLayer / addDimensionStyle /
// addMultileaderStyle already dupe-check before dispatch); the AI safe contour
// dispatches raw EditorCommands DIRECTLY (bypassing every wrapper — see
// `aiDispatcher.ts` / `aiSafeContour.ts`), so an unguarded reducer would let an
// AI-authored CREATE_LAYER silently corrupt the table with two same-named rows.
// The no-op-on-miss convention already established by delete/rename above is
// reused rather than throwing, keeping ALL style-table commands idempotent on
// invalid input (single behavioural contract for the whole family).
//
// Mutation contract per ENTITY-REMAP case:
//   - mutations.updated     : one record per remapped entity (BOTH spaces
//                             AND BEDIT synthetic space — we walk all
//                             spaces, no Model-only filter).
//   - mutations.styleTablesTouched : Set with one of
//                             "dimensionStyles" / "multileaderStyles" /
//                             "layers" depending on the case.
//   - mutations.added/removed : always empty (no entity creation/deletion).
//
// Validation guards (Standard-not-deletable, layer "0" not deletable,
// current-layer protection, missing-name rejection, name-collision
// rejection) live in the editorStore wrapper BEFORE dispatch — the
// reducer assumes inputs have already been validated. If the reducer
// is invoked with a non-existent style/layer name (e.g. via
// command-line dispatch that bypasses the wrapper) the entity scan
// remaps zero entities and the styleTables filter / map produces an
// identical array, leaving the document unchanged.
import {
  assembleDrawingDocument,
  layoutSchema,
  type BlockDefinition,
  type DrawingDocument,
  type DrawingEntity,
  type Layout,
  type LayoutPlotSettings,
  type RawEntityGroup,
  type TextStyle,
} from "@dxf-ai-editor/drawing-model";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";
// The ONE-TRUTH answer to "does this change the laid-out block groups 42/43
// measure" — the TEXT STYLE half of `command-engine::mtextLayoutMeasurement`,
// kept beside the ENTITY half there rather than copied here (@codex PR #794
// round 15 P2; see `sweepMtextMeasurementsForStyle` below).
import {
  mtextStyleMeasurementInvalidation,
  textStyleLayoutChanged,
} from "@dxf-ai-editor/command-engine";

import { diffDocumentMutations } from "./geometryMutationHelpers";
import {
  canonicalStandardDimStyleName,
  namesEqualCaseInsensitive,
} from "./editorStoreInitialState";
import {
  NO_MUTATIONS,
  type EntityMutation,
  type ReducerMutationResult,
  type StyleTableTouchKind,
} from "./types";

/**
 * The four named-object style-table array keys on `DrawingDocument.styleTables`.
 * Each carries records with a `name` key (dim/mleader/text styles + layers).
 */
type StyleTableKey =
  | "dimensionStyles"
  | "multileaderStyles"
  | "textStyles"
  | "layers";

/** A record of ANY of the four style tables (all carry a `name` key). */
type StyleTableEntry = { name: string } & Record<string, unknown>;

/**
 * Lane J (D3-1) — the `EntityMutation` for a styleTables-ONLY change (add/update
 * a NAMED record; NO entity remap). `styleTablesTouched` carries the one table
 * kind so `commitMutation` + the dependency index + the DimStyle/Layer managers
 * react; the entity arrays stay empty (no entity added/removed/updated).
 */
function styleTableTouchMutation(kind: StyleTableTouchKind): EntityMutation {
  return {
    added: [],
    removed: [],
    updated: [],
    styleTablesTouched: new Set<StyleTableTouchKind>([kind]),
  };
}

/** Read one style-table array off the document (shared `?? []` guard). */
function readStyleTable(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
): readonly StyleTableEntry[] {
  return (doc.styleTables?.[tableKey] ?? []) as readonly StyleTableEntry[];
}

/**
 * Lane J — commit a styleTables-only edit: immutably replace the whole table
 * array, re-assemble (Zod re-parse re-validates the record shape the contract's
 * `z.unknown()` payload deliberately left opaque — the one-truth validation
 * boundary), report the single touched table kind.
 */
function commitStyleTableEdit(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
  kind: StyleTableTouchKind,
  nextEntries: readonly StyleTableEntry[],
): ReducerMutationResult {
  const next = assembleDrawingDocument({
    ...doc,
    styleTables: {
      ...doc.styleTables,
      [tableKey]: nextEntries,
    },
  });
  return { document: next, mutations: styleTableTouchMutation(kind) };
}

/**
 * Lane J (D3-1) — the ONE-TRUTH forward body of the three direct-set `add*`
 * commands (`header/add-dimension-style` / `header/add-multileader-style` /
 * `header/add-layer`). The editorStore wrapper already validated (empty/dupe
 * name) and constructed the FULLY-resolved record (base-style clone, imported-
 * handle strip — SL-4/SL-5), so the reducer just appends it.
 *
 * T7 (agent-bridge) correction — a command-line/AI bypass that appends a
 * duplicate name is NOT "caught by the Zod re-parse's uniqueness invariant":
 * `styleTablesSchema` (model.ts) has no `.refine` enforcing unique `name`s
 * across `dimensionStyles`/`multileaderStyles`/`layers`, and `PROD`'s
 * `assembleDrawingDocument` skips the Zod parse entirely (`validateDrawingModel`
 * checks only entity-id/spaceId/activeSpaceId uniqueness — see model.ts). A
 * duplicate would therefore silently double-append in production. The AI safe
 * contour dispatches `header/add-layer` etc. directly (bypassing the wrapper —
 * see `aiDispatcher.ts`), so this reducer is now the fail-closed backstop:
 * idempotent no-op (mirrors the delete/rename cases' "idempotent on miss"
 * contract) when `newEntry.name` already names a row — AND (local Codex
 * round-1 P2, mirrors the SAME fix on `header/rename-layer`) when
 * `newEntry.name` is blank/whitespace-only, so a bypass that slips past
 * BOTH the wrapper AND the AI schema's `.trim().min(1)` (`layerSchemas.ts`)
 * still cannot append an unusable blank-named row. Local Codex round-21 P2 —
 * the duplicate check is CASE-INSENSITIVE (`namesEqualCaseInsensitive`), so
 * `CREATE_LAYER "walls"` cannot slip past as a "new" row when "Walls"
 * already exists.
 */
function appendStyleTableEntry(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
  kind: StyleTableTouchKind,
  newEntry: StyleTableEntry,
): ReducerMutationResult {
  const entries = readStyleTable(doc, tableKey);
  if (
    !newEntry.name.trim() ||
    entries.some((e) => namesEqualCaseInsensitive(e.name, newEntry.name))
  ) {
    return { document: doc, mutations: NO_MUTATIONS };
  }
  return commitStyleTableEdit(doc, tableKey, kind, [...entries, newEntry]);
}

/**
 * Lane J (D3-1) — the ONE-TRUTH forward body of the four direct-set `update*`
 * commands (`header/update-dimension-style` / `header/update-multileader-style`
 * / `header/update-text-style` / `header/update-layer`): find the named record,
 * shallow-merge the partial `updates`, commit. No-op (returns the document
 * UNCHANGED + NO_MUTATIONS, so the dispatch `docMutated` gate records no history
 * entry) when the record is absent OR the merge changes no field — the exact
 * no-op contract the pre-Lane-J `isUnchangedShallowUpdate` direct-set guard
 * enforced (kept here so an idle DIMSTYLE-dialog keystroke pushes no undo entry
 * and bumps no revision). JSON compare — the style/layer fields are scalars +
 * small records, same convention as the shell property reducers.
 *
 * PUBLIC UPDATE SEMANTICS — a partial `update` is "set THESE fields, leave the
 * rest alone", so an explicit `undefined` VALUE means "do not change this
 * field", NOT "clear it to undefined". Callers build patches from optional
 * form / automation fields (e.g. `{ color: undefined }`, or an optional style
 * field left blank), and those absent-value keys must NOT overwrite the
 * existing record or fabricate a history entry. Filter `undefined`-valued keys
 * BEFORE the merge — matching the pre-Lane-J `isUnchangedShallowUpdate` helper,
 * which skipped `undefined` keys (`if (nextVal === undefined) continue;`). A
 * dropped-through undefined would otherwise be written into the record and
 * survive the Zod re-parse's default-filler, silently resetting the field.
 *
 * `name` IS NOT A PATCHABLE FIELD (local Codex PR #794 round-22 P1). The public
 * wrappers take `Partial<DimensionStyle>` / `Partial<TextStyle>` /
 * `Partial<MultileaderStyle>` / `Partial<LayerDefinition>`, so an unrestricted
 * spread let a caller smuggle `name` into a generic patch and RENAME a
 * symbol-table row through a path that performs NONE of the work a rename
 * needs: the cascade over every referencing entity (`entity.layer`,
 * `Dimension.styleName`, `Multileader.styleName`, `Viewport.frozenLayers`,
 * block definitions + raw group-8 tags) and the collision / mandatory-row
 * checks. `updateLayer("Walls", { name: "Roof" })` renamed the row and left
 * every entity stranded on the vanished `Walls`; a text-style rename bundled
 * with a font change additionally made `header/update-text-style`'s
 * measurement sweep key on the NEW name, leaving every MTEXT in the style with
 * stale groups 42/43. Those cascades live ONLY in the dedicated
 * `header/rename-dimension-style` / `header/rename-layer` mutations (see this
 * file's header), so a rename must be dispatched there — the generic patch
 * simply drops the key. Filtering it HERE (the body both `resolveStyleTable-
 * Update` and `isStyleTableUpdateNoOp` share) keeps the reducer's write and the
 * wrapper's pre-dispatch judgment one truth: a patch whose only field is `name`
 * merges to the stored record unchanged, so BOTH read it as a no-op. The stored
 * row's own spelling therefore survives every generic update — including the
 * differently-cased target the round-21 comment below promises.
 */
function mergeDefinedUpdates(
  current: StyleTableEntry,
  updates: Record<string, unknown>,
): StyleTableEntry {
  const definedUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (key === "name") continue;
    if (value !== undefined) definedUpdates[key] = value;
  }
  return { ...current, ...definedUpdates };
}

function resolveStyleTableUpdate(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
  targetName: string,
  updates: Record<string, unknown>,
): {
  current: StyleTableEntry;
  merged: StyleTableEntry;
  nextEntries: readonly StyleTableEntry[];
} | null {
  const entries = readStyleTable(doc, tableKey);
  // Local Codex PR #794 round-21 — CASE-INSENSITIVE target resolution, matching
  // the `appendStyleTableEntry` duplicate guard, the rename paths and
  // `referenceAudit`'s DUPLICATE_TABLE_ENTRY_NAME audit. AutoCAD "preserves the
  // case of names but does not use the case in comparisons" (Autodesk OARX
  // symbol-table/dictionary naming rules, GUID-83ABF20A-57D4-4AB3-8A49-D91E0F70DBFF;
  // `ezdxf`'s `validator.make_table_key` folds a table key with a bare
  // `name.lower()`), so `updateTextStyle("standard", …)` MUST land on the stored
  // `Standard` row instead of missing it. An exact `===` here made that a SILENT
  // no-op: `isStyleTableUpdateNoOp` (the wrapper's pre-dispatch guard, which
  // shares this resolution) reported "nothing to do" and suppressed the dispatch
  // outright, even though the very same spelling resolves at render time
  // (`buildStyleFontMap`) and in the MTEXT measurement sweep. The stored row's
  // OWN spelling is preserved — the merge starts from `current`, so a
  // differently-cased caller renames nothing.
  const idx = entries.findIndex((e) =>
    namesEqualCaseInsensitive(e.name, targetName),
  );
  if (idx < 0) return null;
  const current = entries[idx]!;
  const merged = mergeDefinedUpdates(current, updates);
  if (JSON.stringify(merged) === JSON.stringify(current)) return null;
  return {
    current,
    merged,
    nextEntries: [...entries.slice(0, idx), merged, ...entries.slice(idx + 1)],
  };
}

function updateStyleTableEntry(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
  kind: StyleTableTouchKind,
  targetName: string,
  updates: Record<string, unknown>,
): ReducerMutationResult {
  const resolved = resolveStyleTableUpdate(doc, tableKey, targetName, updates);
  if (!resolved) return { document: doc, mutations: NO_MUTATIONS };
  return commitStyleTableEdit(doc, tableKey, kind, resolved.nextEntries);
}

/**
 * Lane J (D3-1) — @codex round-2 P2 ("Avoid dispatching same-value style
 * updates"). TRUE when an `header/update-{dimension,multileader,text}-style` /
 * `header/update-layer` command would change NOTHING: the target record is
 * ABSENT, OR every DEFINED (non-`undefined`) field already equals the stored
 * value. The editorStore wrapper calls this BEFORE `get().dispatch(...)` so a
 * DIMSTYLE / Layer / MLEADERSTYLE dialog re-submitting the CURRENT values (an
 * Apply with no edit, or an all-`undefined` optional-field patch) short-circuits
 * to a true no-op — RESTORING the pre-Lane-J direct-set free guard
 * (`isUnchangedShallowUpdate`, which ran BEFORE touching the store). Without this
 * the no-op still traverses the full dispatch pipeline (shell resync + a
 * whole-document `assembleDrawingDocument` re-validate — O(all-entities) on a
 * large / cache-backed drawing, since `dispatchMutations` is empty so the
 * `assembleDrawingDocumentInPlace` fast path is NOT taken) only for the reducer
 * to return NO_MUTATIONS. Shares the `mergeDefinedUpdates` body with the reducer's
 * `updateStyleTableEntry` so the wrapper's pre-dispatch judgment and the reducer's
 * own no-op suppression can never diverge (one truth for the merge + compare).
 */
export function isStyleTableUpdateNoOp(
  doc: DrawingDocument,
  tableKey: StyleTableKey,
  targetName: string,
  updates: Record<string, unknown>,
): boolean {
  // MUST use the SAME case-insensitive resolution as `resolveStyleTableUpdate`
  // (see its comment): a mismatch between the wrapper's pre-dispatch guard and
  // the reducer's own lookup is exactly what turns a differently-cased target
  // into a SILENT no-op rather than a visible one.
  const current = readStyleTable(doc, tableKey).find((e) =>
    namesEqualCaseInsensitive(e.name, targetName),
  );
  if (current === undefined) return true;
  const merged = mergeDefinedUpdates(current, updates);
  return JSON.stringify(merged) === JSON.stringify(current);
}

/**
 * Lane J (D3-1) — SHARED resolution for the `setLayoutPlotSettings` write,
 * called by BOTH the editorStore action wrapper (as its no-op / no-target guard
 * — returns `null` ⇒ the wrapper does NOT dispatch, so the document reference is
 * preserved for the MODEL-tab / unknown-id / same-settings cases) AND the
 * geometry reducer (`header/set-layout-plot-settings`, which re-runs it as the
 * authoritative write). ONE truth for the by-id / by-name lockstep resolution +
 * LAYOUT materialisation (PDF-6.6) that previously lived inline in the direct-
 * set action.
 *
 * Returns the next `layouts` / `layoutsBySpaceId` dicts to write, or `null` when
 * there is no layout target (MODEL tab / unknown id) OR the write would be a
 * no-op (both targets already deep-equal the would-be next objects).
 */
export function resolveLayoutPlotSettingsWrite(
  doc: DrawingDocument,
  spaceId: string,
  settings: LayoutPlotSettings,
): {
  layouts: Record<string, Layout>;
  layoutsBySpaceId: Record<string, Layout>;
} | null {
  const byId = doc.layoutsBySpaceId?.[spaceId];
  const space = doc.spaces.find((s) => s.id === spaceId);
  const isLayoutSpace = space?.kind === "layout";

  const tabNameFromId = spaceId.startsWith("layout:")
    ? spaceId.slice("layout:".length)
    : spaceId;
  const nameCandidates = [space?.name, byId?.name, tabNameFromId].filter(
    (n): n is string => typeof n === "string" && n.length > 0,
  );
  const byNameKey =
    nameCandidates.find((n) => doc.layouts?.[n] !== undefined) ??
    nameCandidates[0];
  const byName = byNameKey ? doc.layouts?.[byNameKey] : undefined;

  // No layout target (MODEL tab / unknown id) — nothing to persist.
  if (!byId && !byName && !isLayoutSpace) return null;

  const writeNameKey = byNameKey ?? tabNameFromId;
  const baseLayout: Layout =
    byId ?? byName ?? layoutSchema.parse({ name: space?.name ?? writeNameKey });

  const nextByIdLayout = { ...baseLayout, plotSettings: settings };
  const nextByNameLayout = { ...baseLayout, plotSettings: settings };

  // No-op suppression: both targets already exist AND deep-equal the would-be
  // next objects (the print dialog re-invokes on open/Apply with the CURRENT
  // settings). A missing target is a MATERIALISATION (a real change), never a
  // no-op.
  if (
    byId !== undefined &&
    byName !== undefined &&
    JSON.stringify(byId) === JSON.stringify(nextByIdLayout) &&
    JSON.stringify(byName) === JSON.stringify(nextByNameLayout)
  ) {
    return null;
  }

  return {
    layoutsBySpaceId: { ...doc.layoutsBySpaceId, [spaceId]: nextByIdLayout },
    layouts: { ...doc.layouts, [writeNameKey]: nextByNameLayout },
  };
}

/**
 * Build an id → entity (with spaceId) lookup over a document. Mirrors
 * the helper in `geometryReducerEdit.ts` — kept private here so the
 * cross-module imports stay minimal.
 */
function indexEntitiesById(
  document: DrawingDocument,
): Map<string, { spaceId: string; entity: DrawingEntity }> {
  const idx = new Map<string, { spaceId: string; entity: DrawingEntity }>();
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      idx.set(entity.id, { spaceId: space.id, entity });
    }
  }
  return idx;
}

/**
 * Build the `mutations.updated` array by walking the post-assemble
 * document and looking up each touched id against the pre-document.
 * `prev` / `next` come from the post-Zod-parse references so consumers
 * see exactly what `commitMutation` will install in the cache.
 *
 * `touchedIds` is the set of entity ids whose layer/styleName was
 * remapped in the spaces[].entities[].map() pass. The set is built BEFORE
 * the assemble call so we don't have to re-scan after Zod parse.
 *
 * Returns `NO_MUTATIONS` when `touchedIds.size === 0` AND
 * `styleTablesTouched.size === 0` (true no-op).
 */
function buildRemapMutation(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  touchedIds: ReadonlySet<string>,
  styleTableKind: StyleTableTouchKind,
): EntityMutation {
  if (touchedIds.size === 0) {
    // Style table changed but no entity touched (e.g. delete a style
    // that no entity referenced). The styleTablesTouched flag still
    // matters for downstream consumers (DimStyle Manager, Layer
    // properties palette), so we return an EntityMutation with a
    // populated touch-set instead of NO_MUTATIONS.
    return {
      added: [],
      removed: [],
      updated: [],
      styleTablesTouched: new Set<StyleTableTouchKind>([styleTableKind]),
    };
  }
  const prevIndex = indexEntitiesById(prevDoc);
  const nextIndex = indexEntitiesById(nextDoc);
  const updated: Array<{
    spaceId: string;
    prev: DrawingEntity;
    next: DrawingEntity;
  }> = [];
  for (const id of touchedIds) {
    const before = prevIndex.get(id);
    const after = nextIndex.get(id);
    // Defensive: if an id reported as updated isn't present in either
    // index something upstream is inconsistent — skip rather than throw
    // so a single bad id doesn't break the whole batch.
    if (!before || !after) continue;
    updated.push({
      spaceId: after.spaceId,
      prev: before.entity,
      next: after.entity,
    });
  }
  return {
    added: [],
    removed: [],
    updated,
    styleTablesTouched: new Set<StyleTableTouchKind>([styleTableKind]),
  };
}

/**
 * T7 (agent-bridge layer-mgmt), local Codex round-10 P2 — remap the DXF
 * group-8 (layer name) value inside a `RawEntityGroup[]` array. Imported
 * entities the parser cannot fully type (ACAD_TABLE, legacy SPLINE,
 * XRECORD, ACAD_PROXY_ENTITY, …) are preserved VERBATIM as raw DXF group
 * streams for round-trip fidelity (`RawEntityGroup` — model.ts) — a layer
 * rename that only walked TYPED `entity.layer` / `block.entities[].layer`
 * left these raw siblings still group-8-tagged with the now-vanished
 * `oldLayer`, so a re-export/reload would reference a layer that no longer
 * exists in the table.
 *
 * Returns the SAME array reference when nothing matched (including when
 * `groups` itself is `undefined`) — mirrors `remapBlockLayerReferences`'s
 * reference-identity contract, used the same way by callers to detect
 * "did anything actually change".
 */
function remapRawEntityGroupsLayer(
  groups: RawEntityGroup[] | undefined,
  oldLayer: string,
  newLayer: string,
): RawEntityGroup[] | undefined {
  if (!groups || groups.length === 0) return groups;
  let anyChanged = false;
  const next = groups.map((group) => {
    let groupChanged = false;
    const nextCodes = group.groups.map((g) => {
      // Local Codex round-21 P2 — case-insensitive match (see
      // `namesEqualCaseInsensitive`'s doc comment); an imported raw group can
      // legally carry a layer tag that differs only in case from the table row.
      if (g.code === 8 && namesEqualCaseInsensitive(g.value, oldLayer)) {
        groupChanged = true;
        return { ...g, value: newLayer };
      }
      return g;
    });
    if (!groupChanged) return group;
    anyChanged = true;
    return { ...group, groups: nextCodes };
  });
  return anyChanged ? next : groups;
}

/**
 * T7 (agent-bridge layer-mgmt), local Codex round-2 P2 (+ round-4 P2, +
 * round-10 P2) — remap every layer reference living inside
 * `document.blocks[name]`: the block DEFINITION's own `.layer` field, its
 * TYPED child entities' `.layer` (`document.blocks[name].entities[]`), AND
 * its RAW (unsupported, verbatim-preserved) children's group-8 layer tag
 * (`document.blocks[name].rawEntityGroups[]`) — not just space entities.
 *
 *   - `block.layer` — `blockDefinitionSchema.layer` (model.ts), the BLOCK_
 *     RECORD/BLOCK's own DXF group-8 layer (defaults to "0" but a block CAN
 *     legally be authored on a named layer). Round-4 fix: the round-2 pass
 *     only remapped child entities, leaving a block authored directly on the
 *     renamed layer (with zero matching children) referencing the now-
 *     vanished old name.
 *   - `block.entities[].layer` — a block can legally contain CHILD entities
 *     on a named layer too (the DXF `BLOCKS` section has the identical
 *     per-entity structure as `ENTITIES` — see `blockChildEntitySchema`'s
 *     module comment).
 *   - `block.rawEntityGroups[]` — round-10 fix — a block can also legally
 *     contain UNSUPPORTED children (ACAD_TABLE, legacy SPLINE, …) preserved
 *     verbatim; see `remapRawEntityGroupsLayer`'s doc comment.
 * A layer rename that only walked `spaces[]` would leave any of these stale.
 *
 * Returns the SAME `blocks` reference (`doc.blocks`) when nothing matched —
 * the caller uses reference identity to decide whether the forward mutation
 * touched any block (and therefore whether the undo snapshot needs to carry
 * `blocks` — see `header/rename-layer` below and its `header/restore-style-
 * tables-snapshot` inverse).
 *
 * NOT wired into `EntityMutation.updated`: that contract's `{spaceId, prev,
 * next}` shape has no representation for "lives inside a block definition,
 * not a space" (`EntityMutation` — apps/web/src/app/state/types.ts) — block
 * definitions (and their children) are NOT compact-cache / index-backed the
 * way space entities are, so correctness here is carried entirely by the
 * DOCUMENT CONTENT (this function's output) + the snapshot-restore undo
 * path, not by the mutation-tracking side channel.
 */
function remapBlockLayerReferences(
  blocks: Record<string, BlockDefinition>,
  oldLayer: string,
  newLayer: string,
): Record<string, BlockDefinition> {
  let anyBlockChanged = false;
  // @codex T1 arm(c) round-25 P2 finding (Fix 10, applied here for consistency — a `blocks`
  // record has the IDENTICAL write-side hazard `captureSnapshot.ts`'s `dimensionStyles` table did):
  // `Object.create(null)`, not a `{}` literal — assigning `nextBlocks["__proto__"] = block` on a
  // plain object literal would silently reassign the object's own prototype instead of creating a
  // real entry, dropping a block genuinely named `"__proto__"` (a schema-legal AutoCAD symbol
  // name) from the remapped table. Defensive-in-depth regardless of whether such a name could
  // reach this function today.
  const nextBlocks: Record<string, BlockDefinition> = Object.create(null) as Record<string, BlockDefinition>;
  for (const [name, block] of Object.entries(blocks)) {
    let blockChanged = false;
    const nextEntities = block.entities.map((entity) => {
      // Local Codex round-21 P2 — case-insensitive match (see
      // `namesEqualCaseInsensitive`'s doc comment).
      if (namesEqualCaseInsensitive(entity.layer, oldLayer)) {
        blockChanged = true;
        return { ...entity, layer: newLayer };
      }
      return entity;
    });
    // Round-4 P2 — the block DEFINITION's OWN layer, independent of whether
    // any child entity also matched. Round-21 P2 — case-insensitive match.
    const blockOwnLayerChanged = namesEqualCaseInsensitive(
      block.layer,
      oldLayer,
    );
    if (blockOwnLayerChanged) blockChanged = true;
    // Round-10 P2 — the block's RAW (unsupported) children's group-8 tag.
    const nextRawEntityGroups = remapRawEntityGroupsLayer(
      block.rawEntityGroups,
      oldLayer,
      newLayer,
    );
    const rawEntityGroupsChanged =
      nextRawEntityGroups !== block.rawEntityGroups;
    if (rawEntityGroupsChanged) blockChanged = true;
    if (blockChanged) {
      anyBlockChanged = true;
      nextBlocks[name] = {
        ...block,
        ...(blockOwnLayerChanged ? { layer: newLayer } : {}),
        entities: nextEntities,
        ...(rawEntityGroupsChanged
          ? { rawEntityGroups: nextRawEntityGroups }
          : {}),
      };
    } else {
      nextBlocks[name] = block;
    }
  }
  return anyBlockChanged ? nextBlocks : blocks;
}

/**
 * @codex PR #794 round 15 P2 — the STYLE-TABLE half of the MTEXT group-42/43
 * invalidation.
 *
 * Groups 42/43 are AutoCAD's OWN MEASUREMENT of an MTEXT's laid-out block, and
 * that block depends on the TEXT STYLE the entity's `styleName` resolves to as
 * much as on the entity's own fields. Round 14 cleared the pair from the ENTITY
 * write contour, keyed on a prev→next diff of the MTEXT's fields — which cannot
 * see this edit at all: `header/update-text-style` rewrites a STYLE RECORD and
 * every MTEXT's `styleName` stays exactly as it was, while the renderer resolves
 * that unchanged name through the NEW `fontName` and paints wider glyphs. The
 * stale measurement then (1) is the SEMANTIC rectangle's width, taken verbatim,
 * and (2) truncates the EXACT select ring (`min(walk, group 42)` — round 13), so
 * painted glyphs fall outside it.
 *
 * WHAT counts as layout-affecting is NOT decided here: `command-engine::
 * textStyleLayoutChanged` owns that classification next to the entity one, so
 * the two cannot drift (a second list in this reducer is exactly how round 14
 * and round 15 became two rounds). HOW the clear reaches the document is this
 * function's job, and it is the ordinary style-table entity-remap shape — the
 * same `touchedIds` + `buildRemapMutation` contour `header/delete-layer` and
 * `header/rename-*` use, so ONE `EntityMutation` carries every cleared pair and
 * `commitMutation` syncs the compact cache and the indexes from it.
 *
 * BLOCK DEFINITIONS are swept alongside the spaces, for the reason
 * `remapBlockLayerReferences` sweeps them for a layer rename: `blockChild-
 * EntitySchema` admits MTEXT, a block-child MTEXT carries its own 42/43 and its
 * own `styleName`, and the block bounds path reads `mtextBoxSize` for it. They
 * are NOT representable in `EntityMutation.updated` (no `spaceId`), so — exactly
 * as for the layer rename — correctness there is carried by the document content
 * plus the snapshot-restore undo, and `editorStore.ts` gates the `blocks` carry
 * on this function returning a DIFFERENT `blocks` reference.
 *
 * Returns the same `spaces` / `blocks` references when nothing matched, so the
 * caller can use reference identity as the "did anything change" signal.
 */
function sweepMtextMeasurementsForStyle(
  doc: DrawingDocument,
  styleName: string,
): {
  spaces: DrawingDocument["spaces"];
  blocks: Record<string, BlockDefinition>;
  touchedIds: ReadonlySet<string>;
} {
  const touchedIds = new Set<string>();
  let anySpaceChanged = false;
  const nextSpaces = doc.spaces.map((space) => {
    let spaceChanged = false;
    const entities = space.entities.map((entity) => {
      const clear = mtextStyleMeasurementInvalidation(entity, styleName);
      if (!clear) return entity;
      spaceChanged = true;
      touchedIds.add(entity.id);
      return { ...entity, ...clear };
    });
    if (!spaceChanged) return space;
    anySpaceChanged = true;
    return { ...space, entities };
  });

  let anyBlockChanged = false;
  // `Object.create(null)` rather than a `{}` literal — the same write-side
  // hazard `remapBlockLayerReferences` documents: a block legitimately named
  // `"__proto__"` would otherwise reassign the prototype instead of becoming an
  // entry, dropping it from the table.
  const nextBlocks: Record<string, BlockDefinition> = Object.create(
    null,
  ) as Record<string, BlockDefinition>;
  for (const [name, block] of Object.entries(doc.blocks)) {
    let blockChanged = false;
    const entities = block.entities.map((entity) => {
      const clear = mtextStyleMeasurementInvalidation(entity, styleName);
      if (!clear) return entity;
      blockChanged = true;
      return { ...entity, ...clear };
    });
    if (!blockChanged) {
      nextBlocks[name] = block;
      continue;
    }
    anyBlockChanged = true;
    nextBlocks[name] = { ...block, entities };
  }

  return {
    spaces: anySpaceChanged ? nextSpaces : doc.spaces,
    blocks: anyBlockChanged ? nextBlocks : doc.blocks,
    touchedIds,
  };
}

/**
 * Phase 5 — mutation-tracking entry point for the four style/layer
 * entity-remap commands. Returns a `ReducerMutationResult` for any of
 * the four cases this file owns, or `null` for unrelated commands so
 * the chain in `geometryReducer.ts` keeps falling through.
 *
 * All four cases share the same shape:
 *   1. Walk every space's entities, remap matching ones in-place.
 *   2. Update the styleTables (filter / map / rename key).
 *   3. Run `assembleDrawingDocument` once at the end so Zod re-parses
 *      the whole graph (matches the pattern used by other reducers).
 *   4. Build the `EntityMutation` against the post-assemble doc so
 *      `prev` / `next` references match what `commitMutation` will
 *      install in the cache.
 */
export function applyStyleTablesActionWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): ReducerMutationResult | null {
  switch (command.type) {
    case "header/delete-dimension-style": {
      const trimmedName = command.styleName.trim();
      // Reducer assumes the editorStore wrapper rejected
      // trimmedName === "Standard" and missing-name cases. If a
      // command-line dispatch bypasses the wrapper we still apply the
      // operation idempotently — the filter/map below produces an
      // identical document when nothing matches.
      const remaining = (
        geometryDocument.styleTables?.dimensionStyles ?? []
      ).filter((style) => style.name !== trimmedName);
      // @codex #499 P2 — remap orphaned dimensions to the table's CANONICAL
      // "Standard" spelling (folds case), NOT the literal `"Standard"`. An
      // imported drawing may spell its mandatory style `STANDARD`/mixed case; a
      // literal `"Standard"` points the remapped dimension at a name no row
      // carries, so DIMVAR enrichment falls through instead of resolving the real
      // standard style. `remaining` still holds the mandatory style (it cannot be
      // deleted), so this resolves the ACTUAL spelling — the same target the
      // current-DIMSTYLE pointer reconcile uses.
      const canonicalStandard = canonicalStandardDimStyleName(remaining);
      const touchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          if (
            entity.type === "Dimension" &&
            entity.styleName === trimmedName
          ) {
            touchedIds.add(entity.id);
            return { ...entity, styleName: canonicalStandard };
          }
          return entity;
        }),
      }));
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: nextSpaces,
        styleTables: {
          ...geometryDocument.styleTables,
          dimensionStyles: remaining,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          touchedIds,
          "dimensionStyles",
        ),
      };
    }

    case "header/rename-dimension-style": {
      const trimmedOld = command.oldName.trim();
      const trimmedNew = command.newName.trim();
      // Wrapper guards: oldName !== "Standard", newName non-empty,
      // newName not already present, oldName actually exists. Reducer
      // is idempotent on miss (filter/map produce identical results).
      const renamedStyles = (
        geometryDocument.styleTables?.dimensionStyles ?? []
      ).map((style) =>
        style.name === trimmedOld ? { ...style, name: trimmedNew } : style,
      );
      const touchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          if (entity.type === "Dimension" && entity.styleName === trimmedOld) {
            touchedIds.add(entity.id);
            return { ...entity, styleName: trimmedNew };
          }
          return entity;
        }),
      }));
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: nextSpaces,
        styleTables: {
          ...geometryDocument.styleTables,
          dimensionStyles: renamedStyles,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          touchedIds,
          "dimensionStyles",
        ),
      };
    }

    case "header/delete-multileader-style": {
      const trimmedName = command.styleName.trim();
      const remaining = (
        geometryDocument.styleTables?.multileaderStyles ?? []
      ).filter((style) => style.name !== trimmedName);
      const touchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          if (
            entity.type === "Multileader" &&
            entity.styleName === trimmedName
          ) {
            touchedIds.add(entity.id);
            return { ...entity, styleName: "Standard" };
          }
          return entity;
        }),
      }));
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: nextSpaces,
        styleTables: {
          ...geometryDocument.styleTables,
          multileaderStyles: remaining,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          touchedIds,
          "multileaderStyles",
        ),
      };
    }

    case "header/delete-layer": {
      const trimmedName = command.layerName.trim();
      // Wrapper guards: layerName !== "0", not the current layer, must
      // exist. Reducer is idempotent on miss.
      const remaining = (
        geometryDocument.styleTables?.layers ?? []
      ).filter((layer) => layer.name !== trimmedName);
      const touchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          if (entity.layer === trimmedName) {
            touchedIds.add(entity.id);
            return { ...entity, layer: "0" };
          }
          return entity;
        }),
      }));
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: nextSpaces,
        styleTables: {
          ...geometryDocument.styleTables,
          layers: remaining,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          touchedIds,
          "layers",
        ),
      };
    }

    // T7 (agent-bridge layer-mgmt) — LAYER rename. Mirrors
    // `header/rename-dimension-style` (table rename + cascade rename of every
    // referencing entity) rather than `header/delete-layer` (reassign-to-"0"):
    // a LAYER-table rename is a rename of the SAME layer, not a reassignment of
    // an entity to a DIFFERENT layer — so it is NOT the CHPROP/PROPERTIES
    // per-entity property-edit channel ТЗ §10.1 marks `aiCallable:false` (T5
    // gate). Every entity that was "on" `oldName` stays "on" the (renamed)
    // layer, exactly like AutoCAD's LAYER Rename.
    case "header/rename-layer": {
      const trimmedOld = command.oldName.trim();
      const trimmedNew = command.newName.trim();
      const currentLayers = geometryDocument.styleTables?.layers ?? [];
      // Local Codex round-21 P2 (github PR #527 review) — resolve `oldName`
      // CASE-INSENSITIVELY (AutoCAD layer lookups fold case — see
      // `namesEqualCaseInsensitive`'s doc comment) to the ACTUAL table row,
      // then match every downstream check/remap against THAT row (by
      // reference for the table edit, by its canonical spelling for the
      // entity-remap comparisons) rather than the caller-supplied casing.
      const existingOldLayer = currentLayers.find((l) =>
        namesEqualCaseInsensitive(l.name, trimmedOld),
      );
      const oldExists = existingOldLayer !== undefined;
      // Round-21 P2 — a `newName` that is a case-variant of a DIFFERENT live
      // row is a real collision (AutoCAD would refuse it); a `newName` that
      // is merely a case-variant of the row BEING renamed (a legitimate
      // recase, e.g. "Walls" -> "walls") is NOT a collision with itself —
      // excluded by reference, not by string comparison, so the recase case
      // still proceeds.
      const newNameTaken = currentLayers.some(
        (l) =>
          l !== existingOldLayer && namesEqualCaseInsensitive(l.name, trimmedNew),
      );
      // Idempotent on miss (mirrors every sibling above): layer "0" cannot be
      // renamed (AutoCAD parity — mirrors "Standard" for DIMSTYLE); newName
      // must be non-blank AFTER trim (local Codex round-1 P2 — without this,
      // a whitespace-only `newName` trims to "" and, since "" is neither
      // trimmedOld nor an already-taken name, would slip past every OTHER
      // guard and rename the layer + every entity on it to an empty string);
      // oldName must exist; newName must be non-colliding; a self-rename is
      // a true no-op. Round-21 P2 — `newName === "0"` is ALSO rejected: layer
      // "0" is mandatory (always present), so `newNameTaken` already catches
      // this via the ordinary collision path, but the explicit literal stays
      // as defense-in-depth documentation, mirroring `trimmedOld === "0"`.
      // The editorStore wrapper (renameLayer) + the AI schema
      // (`layerSchemas.ts`, `.trim().min(1)` + the round-21 `newName !== "0"`
      // refine) already enforce these BEFORE dispatch — this is the
      // fail-closed backstop for any caller (AI safe contour, future
      // command-line typed entry) that dispatches this command directly,
      // bypassing both.
      //
      // Coordinator sweep (github PR #527, GraphQL live-finding pass) — the
      // self-rename check compares `trimmedNew` against `existingOldLayer
      // .name` (the row's CANONICAL stored spelling), NOT the raw
      // `trimmedOld` param. A bare `trimmedOld === trimmedNew` silently
      // no-opped a legitimate case-NORMALIZING rename: table has "Walls",
      // caller dispatches `oldName:"walls", newName:"walls"` (both
      // lower-case) — `existingOldLayer` still resolves to the "Walls" row
      // (case-insensitive lookup above), but "walls" === "walls" is TRUE, so
      // the recase the caller actually wanted (Walls -> walls) never
      // applied, and the caller got a false "success". Comparing against
      // the CANONICAL name ("Walls" !== "walls") correctly proceeds with the
      // recase, while a genuine self-rename (oldName/newName both already
      // matching the canonical spelling) still correctly no-ops.
      if (
        trimmedOld === "0" ||
        !trimmedNew ||
        trimmedNew === "0" ||
        !oldExists ||
        newNameTaken ||
        (existingOldLayer !== undefined && existingOldLayer.name === trimmedNew)
      ) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const renamedLayers = currentLayers.map((layer) =>
        layer === existingOldLayer ? { ...layer, name: trimmedNew } : layer,
      );
      const touchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => ({
        ...space,
        entities: space.entities.map((entity) => {
          const layerChanged = namesEqualCaseInsensitive(
            entity.layer,
            trimmedOld,
          );
          // Local Codex round-8 P2 — a Viewport's OWN `frozenLayers` (per-
          // viewport frozen-layer NAMES, ТЗ §9 "Viewport layer overrides[]"
          // — DISTINCT from `frozenLayerHandles`, the imported-DXF handle-
          // based carrier, which is handle-keyed and therefore rename-
          // stable) can ALSO name the renamed layer, independent of
          // `entity.layer`. Remap it alongside `entity.layer` so filtering/
          // export paths that read `frozenLayers` by name stay correct.
          if (entity.type === "Viewport") {
            // Round-21 P2 — case-insensitive match, same reasoning as
            // `layerChanged` above.
            const frozenLayersChanged = entity.frozenLayers.some((name) =>
              namesEqualCaseInsensitive(name, trimmedOld),
            );
            if (!layerChanged && !frozenLayersChanged) return entity;
            touchedIds.add(entity.id);
            return {
              ...entity,
              ...(layerChanged ? { layer: trimmedNew } : {}),
              ...(frozenLayersChanged
                ? {
                    frozenLayers: entity.frozenLayers.map((name) =>
                      namesEqualCaseInsensitive(name, trimmedOld)
                        ? trimmedNew
                        : name,
                    ),
                  }
                : {}),
            };
          }
          if (!layerChanged) return entity;
          touchedIds.add(entity.id);
          return { ...entity, layer: trimmedNew };
        }),
      }));
      // Local Codex round-2 + round-4 + round-10 P2 — also remap BLOCK-
      // DEFINITION own layer + typed/raw children (see
      // `remapBlockLayerReferences`'s doc comment). Same-reference no-op
      // when nothing referenced `trimmedOld` (editorStore.ts's
      // inverse-building code detects the change via
      // `preMutationDoc.blocks !== nextGeometryDocument.blocks` to decide
      // whether the undo snapshot needs to carry `blocks`).
      const nextBlocks = remapBlockLayerReferences(
        geometryDocument.blocks,
        trimmedOld,
        trimmedNew,
      );
      // Local Codex round-10 P2 — the top-level ENTITIES-section RAW
      // (unsupported, verbatim-preserved) siblings — the document-level
      // analogue of `block.rawEntityGroups` above (see
      // `remapRawEntityGroupsLayer`'s doc comment). Same-reference no-op
      // when nothing referenced `trimmedOld`; editorStore.ts's
      // inverse-building code mirrors the `blocks` reference-identity gate
      // to decide whether the undo snapshot needs to carry
      // `rawEntityGroups`.
      const nextRawEntityGroups = remapRawEntityGroupsLayer(
        geometryDocument.rawEntityGroups,
        trimmedOld,
        trimmedNew,
      );
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: nextSpaces,
        blocks: nextBlocks,
        ...(nextRawEntityGroups !== geometryDocument.rawEntityGroups
          ? { rawEntityGroups: nextRawEntityGroups }
          : {}),
        styleTables: {
          ...geometryDocument.styleTables,
          layers: renamedLayers,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          touchedIds,
          "layers",
        ),
      };
    }

    // ── Lane J (D3-1) — direct-set → dispatch style-table ADD commands ──────
    case "header/add-dimension-style":
      return appendStyleTableEntry(
        geometryDocument,
        "dimensionStyles",
        "dimensionStyles",
        command.style as StyleTableEntry,
      );
    case "header/add-multileader-style":
      return appendStyleTableEntry(
        geometryDocument,
        "multileaderStyles",
        "multileaderStyles",
        command.style as StyleTableEntry,
      );
    case "header/add-layer":
      return appendStyleTableEntry(
        geometryDocument,
        "layers",
        "layers",
        command.layer as StyleTableEntry,
      );

    // ── Lane J (D3-1) — direct-set → dispatch style-table UPDATE commands ───
    case "header/update-dimension-style":
      return updateStyleTableEntry(
        geometryDocument,
        "dimensionStyles",
        "dimensionStyles",
        command.styleName,
        command.updates,
      );
    case "header/update-multileader-style":
      return updateStyleTableEntry(
        geometryDocument,
        "multileaderStyles",
        "multileaderStyles",
        command.styleName,
        command.updates,
      );
    // @codex PR #794 round 15 P2 — the ONE style-table update that can also
    // invalidate ENTITY state. A text style's font / fixed height / width
    // factor / oblique / vertical flag / charset ARE the glyph metrics every
    // MTEXT drawn in that style is laid out with, so changing one makes those
    // MTEXTs' cached groups 42/43 a measurement of a block that no longer
    // exists — while their own `styleName` is untouched, so round 14's ENTITY
    // field diff cannot see it. Same forward transition, same
    // `EntityMutation`, same `commitMutation`: the cleared pairs ride the
    // ordinary `buildRemapMutation` contour the delete/rename siblings use, and
    // `editorStore.ts`'s snapshot inverse restores the style row AND the
    // measurements in ONE history entry. A NON-layout style edit (a rename of
    // nothing, `lastHeight`, the round-trip handles) stays byte-identical to
    // the shared `updateStyleTableEntry` path.
    case "header/update-text-style": {
      const resolved = resolveStyleTableUpdate(
        geometryDocument,
        "textStyles",
        command.styleName,
        command.updates,
      );
      if (!resolved) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      if (
        !textStyleLayoutChanged(
          resolved.current as unknown as TextStyle,
          resolved.merged as unknown as TextStyle,
        )
      ) {
        return commitStyleTableEdit(
          geometryDocument,
          "textStyles",
          "textStyles",
          resolved.nextEntries,
        );
      }
      // The style row's CANONICAL stored spelling, not the caller's casing:
      // `mtextStyleMeasurementInvalidation` folds case (AutoCAD symbol lookups
      // do, and so does the renderer's own `buildStyleFontMap`), so either
      // spelling selects the same entities — but passing the row's own name
      // keeps the sweep keyed on the document, not on the command payload.
      const sweep = sweepMtextMeasurementsForStyle(
        geometryDocument,
        resolved.merged.name,
      );
      const nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: sweep.spaces,
        blocks: sweep.blocks,
        styleTables: {
          ...geometryDocument.styleTables,
          textStyles: resolved.nextEntries,
        },
      });
      return {
        document: nextDoc,
        mutations: buildRemapMutation(
          geometryDocument,
          nextDoc,
          sweep.touchedIds,
          "textStyles",
        ),
      };
    }
    case "header/update-layer":
      return updateStyleTableEntry(
        geometryDocument,
        "layers",
        "layers",
        command.layerName,
        command.updates,
      );

    // ── Lane J (D3-1) — PDF-6.6 per-layout plot settings (moved from the
    // direct-set action). The shared `resolveLayoutPlotSettingsWrite` owns the
    // by-id / by-name lockstep + LAYOUT materialisation; a `null` result is a
    // no-op (MODEL tab / unknown id / same settings) — the wrapper already
    // guards against dispatching those, but the reducer stays idempotent for a
    // command-line bypass. Layout metadata (no entity payload) → NO_MUTATIONS;
    // `docHeaderChanged`'s layouts fingerprint drives the history + bump.
    case "header/set-layout-plot-settings": {
      const write = resolveLayoutPlotSettingsWrite(
        geometryDocument,
        command.spaceId,
        command.settings as LayoutPlotSettings,
      );
      if (!write) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const next = assembleDrawingDocument({
        ...geometryDocument,
        layouts: write.layouts,
        layoutsBySpaceId: write.layoutsBySpaceId,
      });
      return { document: next, mutations: NO_MUTATIONS };
    }

    // ── Lane J (D3-1) — history-internal undo inverse of EVERY style-table +
    // layout-plot command. Restores the PRE-mutation slices captured by the
    // hand-built inverse in editorStore.ts. Only the slices the forward touched
    // are present (`styleTables` for style/layer edits; `layouts` +
    // `layoutsBySpaceId` for plot settings; `spaces` ONLY for delete/rename
    // entity remaps). `diffDocumentMutations` recomputes the entity + style-
    // table delta so `commitMutation` re-installs the restored entities /
    // style-table refs (mirrors `file/xref-restore-snapshot`).
    case "header/restore-style-tables-snapshot": {
      const restored = assembleDrawingDocument({
        ...geometryDocument,
        ...(command.styleTables !== undefined
          ? {
              styleTables:
                command.styleTables as DrawingDocument["styleTables"],
            }
          : {}),
        ...(command.layouts !== undefined
          ? { layouts: command.layouts as DrawingDocument["layouts"] }
          : {}),
        ...(command.layoutsBySpaceId !== undefined
          ? {
              layoutsBySpaceId:
                command.layoutsBySpaceId as DrawingDocument["layoutsBySpaceId"],
            }
          : {}),
        ...(command.spaces !== undefined
          ? { spaces: command.spaces as DrawingDocument["spaces"] }
          : {}),
        // T7 (agent-bridge layer-mgmt), local Codex round-2 + round-4 P2 —
        // undo half of a `header/rename-layer` that remapped a BLOCK-
        // DEFINITION's own `layer` or a child's `layer` (see
        // `remapBlockLayerReferences`). Present ONLY when the forward
        // actually touched a block (editorStore.ts's inverse builder gates
        // it on `preMutationDoc.blocks !== nextGeometryDocument.blocks`),
        // mirroring `spaces`'s own gating.
        ...(command.blocks !== undefined
          ? { blocks: command.blocks as DrawingDocument["blocks"] }
          : {}),
        // T7 (agent-bridge layer-mgmt), local Codex round-10 P2 — undo half
        // of a `header/rename-layer` that remapped the top-level
        // ENTITIES-section RAW (unsupported) siblings' group-8 layer tag
        // (see `remapRawEntityGroupsLayer`). Present ONLY when the forward
        // actually touched a raw group, mirroring `blocks`'s own gating.
        ...(command.rawEntityGroups !== undefined
          ? {
              rawEntityGroups:
                command.rawEntityGroups as DrawingDocument["rawEntityGroups"],
            }
          : {}),
      });
      // Which of the four style tables changed value (for the dependency index /
      // manager reactivity). Undo/redo is not hot-path; a small 4-key compare.
      const touched = new Set<StyleTableTouchKind>();
      if (command.styleTables !== undefined) {
        const before = geometryDocument.styleTables;
        const after = restored.styleTables;
        for (const kind of [
          "dimensionStyles",
          "multileaderStyles",
          "textStyles",
          "layers",
        ] as const) {
          if (
            JSON.stringify(before?.[kind] ?? []) !==
            JSON.stringify(after?.[kind] ?? [])
          ) {
            touched.add(kind);
          }
        }
      }
      // Lane J perf (@codex P2 "Do not diff every space for table-only undo") —
      // the inverse carries a `spaces` slice ONLY when the forward actually
      // REMAPPED entities (a delete/rename that touched referencing
      // dimensions/multileaders/entities; see the editorStore inverse builder,
      // gated on `dispatchMutations` having updated/removed). When `spaces` is
      // absent the forward touched ONLY the style/layout tables (every add /
      // update / plot edit, plus a delete/rename that remapped ZERO entities),
      // so NO entity was restored. Skip the O(all-spaces) `diffDocumentMutations`
      // walk — which would traverse every space on a large / cache-backed
      // drawing just to confirm nothing changed (a simple Ctrl+Z for a layer
      // colour could materialise the whole document) — and return the already
      // computed style-table touch mutation directly. This EXACTLY matches what
      // `diffDocumentMutations` would produce (identical spaces ⇒ empty entity
      // arrays + the same `touched` set), only without the traversal. An empty
      // `touched` (a layout-plot-only undo) collapses to `NO_MUTATIONS`, mirroring
      // the forward `header/set-layout-plot-settings` (its history / re-render is
      // driven by `docHeaderChanged`'s layouts fingerprint, not the mutation).
      if (command.spaces === undefined) {
        return {
          document: restored,
          mutations:
            touched.size === 0
              ? NO_MUTATIONS
              : { added: [], removed: [], updated: [], styleTablesTouched: touched },
        };
      }
      return {
        document: restored,
        mutations: diffDocumentMutations(geometryDocument, restored, touched),
      };
    }

    default:
      return null;
  }
}
